import {
  getTvTeamFaceSlots,
  type TVTeamFaceSlot,
} from '@/components/tv-mode/tv-team-faces';
import { FixtureType } from '@/types/tournament.model';
import type {
  Club,
  Fixture,
  KnockoutNode,
  Match,
  Team,
  Tournament,
} from '@/types/tournament.model';
import { groupLabel } from './fixture-utils';
import { getTeamName as defaultGetTeamName } from './team-utils';

type TeamNameFormatter = (team: Team | undefined | null) => string;

export type TVUpcomingMatch = {
  id: string;
  startDate: string;
  court: string;
  team1: string;
  team2: string;
  team1Slots: [TVTeamFaceSlot, TVTeamFaceSlot];
  team2Slots: [TVTeamFaceSlot, TVTeamFaceSlot];
  category: string;
  tournamentName: string;
  tournamentIndex: 0 | 1;
};

export type TVResultMatch = {
  id: string;
  startDate: string;
  team1: string;
  team2: string;
  team1Slots: [TVTeamFaceSlot, TVTeamFaceSlot];
  team2Slots: [TVTeamFaceSlot, TVTeamFaceSlot];
  team1Score: string;
  team2Score: string;
  /**
   * Raw set scores so consumers can highlight the per-set winner (green per
   * cell) instead of coloring the whole row by match winner.
   */
  setScores: { team1Games: number; team2Games: number }[];
  team1Won: boolean;
  category: string;
  /**
   * Walkover por equipo: el nombre del equipo con flag = true se renderiza
   * tachado en la tarjeta (sin texto extra).
   */
  team1Walkover?: boolean;
  team2Walkover?: boolean;
};

const isMatchPlayed = (match: Match): boolean =>
  !!match.result && match.result.isValid === true;

const flattenKnockoutMatches = (
  node: KnockoutNode | null | undefined,
  acc: Array<{ match: Match; roundName: string }> = [],
): Array<{ match: Match; roundName: string }> => {
  if (!node) return acc;
  if (node.match) {
    acc.push({
      match: node.match,
      roundName: node.roundName ?? `Ronda ${node.round ?? ''}`.trim(),
    });
  }
  flattenKnockoutMatches(node.leftChild, acc);
  flattenKnockoutMatches(node.rightChild, acc);
  return acc;
};

const collectTournamentMatches = (
  fixture: Fixture | null,
): Array<{ match: Match; category: string; phase: FixtureType }> => {
  const result: Array<{
    match: Match;
    category: string;
    phase: FixtureType;
  }> = [];

  fixture?.groupList?.forEach((group, index) => {
    const groupName = groupLabel(index);
    group.matchList?.forEach((match) => {
      result.push({
        match,
        category: groupName,
        phase: FixtureType.GROUPS_AND_KNOCKOUTS,
      });
    });
  });

  if (fixture?.knockoutTree?.rootNode) {
    flattenKnockoutMatches(fixture.knockoutTree.rootNode).forEach(
      ({ match, roundName }) => {
        result.push({
          match,
          category: roundName,
          phase: FixtureType.KNOCKOUTS,
        });
      },
    );
  }

  return result;
};

const formatCourt = (match: Match): string => {
  const courtName = match.location?.trim();
  const clubName = match.club?.name?.trim();
  if (courtName && clubName) return `${clubName} · ${courtName}`;
  return courtName || clubName || 'Pista por definir';
};

const formatScore = (
  scores: { team1Games: number; team2Games: number }[] | undefined,
  side: 1 | 2,
): string => {
  if (!scores || scores.length === 0) return '-';
  return scores
    .map((s) => (side === 1 ? s.team1Games : s.team2Games))
    .join('  ');
};

export const buildUpcomingMatches = (
  data: Array<{ tournament: Tournament; fixture: Fixture | null }>,
  limit = 6,
  getTeamName: TeamNameFormatter = defaultGetTeamName,
): TVUpcomingMatch[] => {
  const now = Date.now();
  const all: TVUpcomingMatch[] = [];

  data.forEach(({ tournament, fixture }, idx) => {
    const tournamentIndex = (idx === 0 ? 0 : 1) as 0 | 1;
    collectTournamentMatches(fixture).forEach(({ match, category }) => {
      if (isMatchPlayed(match)) return;
      const ts = new Date(match.startDate).getTime();
      if (Number.isNaN(ts) || ts < now) return;
      if (!match.team1 && !match.team2) return;
      all.push({
        id: match.id,
        startDate: match.startDate,
        court: formatCourt(match),
        team1: getTeamName(match.team1),
        team2: getTeamName(match.team2),
        team1Slots: getTvTeamFaceSlots(match.team1),
        team2Slots: getTvTeamFaceSlots(match.team2),
        category,
        tournamentName: tournament.name,
        tournamentIndex,
      });
    });
  });

  return all
    .sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    )
    .slice(0, limit);
};

/** Últimos resultados jugados: más reciente primero, luego `limit`. */
export const buildResultsForTournament = (
  fixture: Fixture | null,
  limit = 10,
  options?: {
    onlyKnockout?: boolean;
  },
  getTeamName: TeamNameFormatter = defaultGetTeamName,
): TVResultMatch[] => {
  return collectTournamentMatches(fixture)
    .filter(({ match, phase }) => {
      if (!isMatchPlayed(match)) return false;
      if (options?.onlyKnockout && phase !== FixtureType.KNOCKOUTS)
        return false;
      return true;
    })
    .map(({ match, category }) => {
      const result = match.result!;
      const team1 = getTeamName(match.team1);
      const team2 = getTeamName(match.team2);
      const team1Walkover = result.team1Walkover === true;
      const team2Walkover = result.team2Walkover === true;
      const isDoubleWalkover = team1Walkover && team2Walkover;
      const team1Won =
        !isDoubleWalkover &&
        (team2Walkover ||
          (!team1Walkover &&
            (result.team1Score ?? 0) > (result.team2Score ?? 0)));
      return {
        id: match.id,
        startDate: match.startDate,
        team1,
        team2,
        team1Slots: getTvTeamFaceSlots(match.team1),
        team2Slots: getTvTeamFaceSlots(match.team2),
        team1Score: formatScore(result.setScores, 1),
        team2Score: formatScore(result.setScores, 2),
        setScores: result.setScores ?? [],
        team1Won,
        category,
        ...(team1Walkover ? { team1Walkover: true } : {}),
        ...(team2Walkover ? { team2Walkover: true } : {}),
      };
    })
    .sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
    )
    .slice(0, limit);
};

export type TVClubInfo = {
  primaryName: string;
  city: string | null;
  multiple: boolean;
  /** Banner / imagen del club cuando hay una sola sede y el backend la envía */
  imageUrl: string | null;
};

export const resolveClubInfo = (
  tournaments: Tournament[],
): TVClubInfo | null => {
  const clubs: Club[] = tournaments
    .flatMap((t) => t.clubs ?? [])
    .filter((c): c is Club => !!c?.name);
  if (clubs.length === 0) return null;

  const uniqueIds = Array.from(new Set(clubs.map((c) => c.id)));
  if (uniqueIds.length === 1) {
    const club = clubs[0];
    return {
      primaryName: club.name,
      city: club.address?.city ?? null,
      multiple: false,
      imageUrl: club.bannerUrl ?? null,
    };
  }

  return {
    primaryName: 'Múltiples sedes',
    city: null,
    multiple: true,
    imageUrl: null,
  };
};

export const formatLiveIn = (startDate: string): string | null => {
  const ts = new Date(startDate).getTime();
  if (Number.isNaN(ts)) return null;
  const diffMin = Math.round((ts - Date.now()) / 60000);
  if (diffMin <= 0 || diffMin > 60) return null;
  return `${diffMin}'`;
};

export const formatTime = (startDate: string): string => {
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return '--:--';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const SHORT_DATE_FMT = new Intl.DateTimeFormat('es', {
  day: 'numeric',
  month: 'short',
});

const isSameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** "Hoy" si coincide con la fecha local actual; si no, "15 mar", "3 abr"… */
export const formatShortDate = (startDate: string): string | null => {
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return null;
  if (isSameLocalDay(d, new Date())) return 'Hoy';
  return SHORT_DATE_FMT.format(d).replace(/\.$/, '');
};
